import { useQuery } from "@tanstack/react-query";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { post } from "common/helpers/HTTP";
import type { LinkedInProfile, ProfileSearchParams } from "common/types";

async function searchProfilesRequest(
  workspaceId: string,
  accountId: string,
  searchParams: object,
): Promise<{
  audience: LinkedInProfile[];
  totalCount: number;
  isLimitExceeded: boolean;
}> {
  const queryParams = new URLSearchParams();
  queryParams.set("page", "0");
  queryParams.set("count", "20");

  const {
    targets: audience,
    total_count: totalCount,
    limit_exceeded: isLimitExceeded,
  } = await post(
    `workspaces/${workspaceId}/accounts/${accountId}/search?${queryParams.toString()}`,
    searchParams,
  );

  return { audience, totalCount, isLimitExceeded };
}

export default function useSearchSourceLeads({
  accountId,
  searchParams,
}: {
  accountId: string;
  searchParams: Partial<ProfileSearchParams>;
}) {
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { leadListSourcesKeys } = getQueryKeys(workspaceId);

  const hasSearchParams = Object.values(searchParams).some((param) =>
    Array.isArray(param) ? param.length : param,
  );

  const {
    data: searchResults,
    isFetching: isFetchingProfiles,
    isError: isProfilesPreviewError,
    refetch: refetchProfilesPreview,
  } = useQuery({
    queryKey: leadListSourcesKeys.profilesPreview(accountId, searchParams),
    queryFn: () => searchProfilesRequest(workspaceId, accountId, searchParams),
    enabled: !!accountId && hasSearchParams,
    placeholderData: (previousData, previousQuery) => {
      if (previousData && previousQuery) {
        const previousSearchParams = previousQuery.queryKey[4];

        if (hasSearchParams && previousSearchParams === searchParams) {
          return previousData;
        }
      }
    },
    gcTime: 0,
  });

  return {
    profiles: searchResults?.audience,
    totalCount: searchResults?.totalCount,
    isLimitExceeded: searchResults?.isLimitExceeded,
    isFetchingProfiles,
    isProfilesPreviewError,
    refetchProfilesPreview,
  };
}
